import type { CSSProperties, ReactNode } from "react";
import { Icon, type IconName } from "./Icon";

/* ── Avatar ─────────────────────────────────────────────────────────────
 * Initials on a navy chip. Used for staff/user identity in the topbar & lists.
 */
export interface AvatarProps {
  name?: string;
  email?: string;
  size?: "sm" | "md" | "lg";
}

function initials(name?: string, email?: string): string {
  const src = (name || email || "?").trim();
  if (src.includes("@")) return src[0]!.toUpperCase();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function Avatar({ name, email, size = "md" }: AvatarProps) {
  return (
    <span className={`fd-avatar fd-avatar--${size}`} aria-hidden="true">
      {initials(name, email)}
    </span>
  );
}

/* ── Stat (KPI tile) ────────────────────────────────────────────────────
 * Richer than Metric: an icon chip + eyebrow label + big tabular value + an
 * optional trend pill. The content for an elevated Card.
 */
export interface StatProps {
  label: ReactNode;
  value: ReactNode;
  icon?: IconName;
  hint?: ReactNode;
  trend?: { value: string; direction: "up" | "down" | "flat" };
  tone?: "default" | "gold" | "success" | "warning" | "danger";
}

export function Stat({ label, value, icon, hint, trend, tone = "default" }: StatProps) {
  return (
    <div className="fd-stat">
      <div className="fd-stat__top">
        <span className="fd-stat__label">{label}</span>
        {icon && (
          <span className={`fd-stat__icon fd-stat__icon--${tone}`}>
            <Icon name={icon} size={18} />
          </span>
        )}
      </div>
      <div className="fd-stat__value">{value}</div>
      <div className="fd-stat__foot">
        {trend && (
          <span className={`fd-stat__trend fd-stat__trend--${trend.direction}`}>
            {trend.direction !== "flat" && (
              <Icon name={trend.direction === "up" ? "arrowUp" : "arrowDown"} size={13} />
            )}
            {trend.value}
          </span>
        )}
        {hint && <span className="fd-stat__hint">{hint}</span>}
      </div>
    </div>
  );
}

/* ── DescriptionList ────────────────────────────────────────────────────
 * Term/value pairs for detail panels (account info, contract terms).
 */
export function DescriptionList({ children, columns = 2 }: { children: ReactNode; columns?: 1 | 2 | 3 }) {
  return (
    <dl className="fd-dl" style={{ "--fd-dl-cols": columns } as CSSProperties}>
      {children}
    </dl>
  );
}

export function DRow({ label, children, mono }: { label: ReactNode; children: ReactNode; mono?: boolean }) {
  return (
    <div className="fd-dl__row">
      <dt className="fd-dl__term">{label}</dt>
      <dd className={["fd-dl__desc", mono && "fd-mono"].filter(Boolean).join(" ")}>{children}</dd>
    </div>
  );
}
